import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiService, type User } from "../services/api";
import { getCookie, setCookie, deleteCookie } from "../utils/cookies";

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const t = getCookie("toddlar_token");
    setToken(t);
    const init = async () => {
      try {
        if (t) {
          const res = await apiService.getProfile();
          if (res.success && res.data) setUser(res.data.user);
        }
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isLoading,
      login: (u: User, t: string) => {
        setUser(u);
        setToken(t);
        setCookie("toddlar_token", t);
      },
      logout: () => {
        setUser(null);
        setToken(null);
        deleteCookie("toddlar_token");
      },
      refreshProfile: async () => {
        const res = await apiService.getProfile();
        if (res.success && res.data) setUser(res.data.user);
      },
    }),
    [user, token, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

